import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getQuizResultsByReference, getUserPerformanceAnalysis, generateUserPerformanceAnalysis, getQuestionWithSolution } from '../components/apiClient';
import { useJWTAuth } from '../components/JWTAuthContext';
import SolutionPopup from '../components/SolutionPopup';
import { MathJaxProvider } from '../components/math-renderers';
import '../components/SolutionPopup/SolutionPopup.css';

interface QuestionResult {
  id: number;
  question_img_url: string;
  has_multiple_answer: boolean;
  duration: number;
  option_count: number;
  exam_id: number;
  subject_id: number;
  chapter_name?: string;
  chapter_id?: number;
  level?: string;
  topic_id?: number;
  correct_option: string;
  solution?: string;
  question_order?: number;
  user_answer?: string;
  score?: number;
  time_taken?: number;
  attempt_date?: string;
  is_correct?: boolean;
  status?: 'correct' | 'incorrect' | 'unanswered';
  explanation?: string;
  options?: { [key: string]: string };
  question_text?: string;
  correct_answer?: string;
}

interface PerformanceAnalysis {
  id: string;
  user_id: string;
  quiz_id: string;
  quiz_attempt_number: string;
  quiz_stats: {
    total_questions: string;
    correct_answers: string;
    incorrect_answers: string;
    unanswered_questions: string;
    accuracy_percentage: string;
    total_time_spent: string;
    average_time_per_question: string;
    completion_rate: number;
    efficiency_score: number;
    performance_grade: string;
  };
  performance_analysis: {
    strongest_subject: string;
    weakest_subject: string;
    strongest_topic: string;
    weakest_topic: string;
    time_management_score: string;
    performance_score: string;
    overall_progress_trend: string;
    time_efficiency_rating: string;
  };
  progress_tracking: {
    previous_quiz_accuracy: string;
    accuracy_improvement: string;
    previous_avg_time: string;
    time_improvement: string;
    is_improvement: boolean;
    is_faster: boolean;
    progress_direction: string;
  };
  ai_insights: {
    recommendations: string[];
    learning_suggestions: string[];
    progress_insights: string[];
    improvement_areas: string[];
    strengths_identified: string[];
    recommendation_count: number;
    strength_count: number;
    improvement_area_count: number;
  };
  detailed_scores: {
    subject_scores: any;
    topic_scores: any;
    subject_progress: any[];
    subject_count: number;
    topic_count: number;
    best_subject_score: number;
    worst_subject_score: number;
    average_subject_score: number;
  };
  learning_insights: {
    mastery_level: string;
    focus_areas: Array<{
      area: string;
      type: string;
      score: number;
      priority: string;
    }>;
    next_steps: Array<{
      action: string;
      priority: string;
      estimated_time: string;
    }>;
    study_time_recommendation: {
      daily_minutes: number;
      focus: string;
      type: string;
    };
    difficulty_readiness: string;
  };
  metadata: {
    difficulty_performance: string;
    analysis_version: string;
    created_at: string;
    updated_at: string;
    analysis_completeness: number;
    data_quality_score: number;
  };
}

interface QuizResultData {
  quiz_reference: string;
  quiz: {
    id: number;
    name: string;
    description: string;
    subject_name: string;
    exam_name: string;
    exam_id: number;
  };
  questions: QuestionResult[];
  stats: {
    total_questions: number;
    answered_questions: number;
    correct_answers: number;
    incorrect_answers: number;
    unanswered_questions: number;
    percentage: number;
    total_score: number;
    max_possible_score: number;
    total_time_taken: number;
    average_time_per_question: number;
  };
}

const DetailedQuizResults: React.FC = () => {
  const { quizReference } = useParams<{ quizReference: string }>();
  const navigate = useNavigate();
  const { user } = useJWTAuth();

  const [quizResults, setQuizResults] = useState<QuizResultData | null>(null);
  const [performanceAnalysis, setPerformanceAnalysis] = useState<PerformanceAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPerformance, setIsLoadingPerformance] = useState(false);
  const [isGeneratingPerformance, setIsGeneratingPerformance] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({
    step: 0,
    totalSteps: 4,
    currentTask: '',
    percentage: 0
  });
  const [performanceNotFound, setPerformanceNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSolutions, setShowSolutions] = useState(false);
  const [showPerformanceInsights, setShowPerformanceInsights] = useState(true);

  // Solution popup state
  const [solutionPopupOpen, setSolutionPopupOpen] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [selectedQuestionImageUrl, setSelectedQuestionImageUrl] = useState<string>('');

  useEffect(() => {
    const fetchQuizResults = async () => {
      if (!quizReference || !user?.id) {
        setError('Quiz reference or user information not found');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch quiz results from the backend API
        const response = await getQuizResultsByReference(quizReference, user.id);

        if (response && response.result) {
          setQuizResults(response.result);

          // Fetch performance analysis if quiz results are available
          if (response.result.quiz && response.result.quiz.id) {
            await fetchPerformanceAnalysis(user.id, response.result.quiz.id);
          }
        } else {
          setError('No quiz results found');
        }
      } catch (err) {
        console.error('Error fetching quiz results:', err);
        setError('Failed to load quiz results');
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuizResults();
  }, [quizReference, user]);

  const fetchPerformanceAnalysis = async (userId: number, quizId: number) => {
    try {
      setIsLoadingPerformance(true);
      setPerformanceNotFound(false);
      const response = await getUserPerformanceAnalysis(userId, quizId);

      if (response && response.status === 'success' && response.result) {
        setPerformanceAnalysis(response.result);
        setPerformanceNotFound(false);
      } else {
        console.log('Performance analysis not available or not yet generated');
        setPerformanceNotFound(true);
      }
    } catch (err) {
      console.log('Performance analysis not available:', err);
      setPerformanceNotFound(true);
    } finally {
      setIsLoadingPerformance(false);
    }
  };

  const handleGeneratePerformanceAnalysis = async () => {
    if (!quizResults?.quiz?.id || !user?.id) return;

    try {
      setIsGeneratingPerformance(true);
      setPerformanceNotFound(false);

      // Step 1: Initializing analysis
      setGenerationProgress({
        step: 1,
        totalSteps: 4,
        currentTask: 'Initializing AI performance analysis...',
        percentage: 25
      });

      // Add small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 800));

      // Step 2: Processing quiz data
      setGenerationProgress({
        step: 2,
        totalSteps: 4,
        currentTask: 'Processing quiz data and answers...',
        percentage: 50
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 3: Generating insights
      setGenerationProgress({
        step: 3,
        totalSteps: 4,
        currentTask: 'Generating AI insights and recommendations...',
        percentage: 75
      });

      const response = await generateUserPerformanceAnalysis(user.id, quizResults.quiz.id);

      // Step 4: Finalizing
      setGenerationProgress({
        step: 4,
        totalSteps: 4,
        currentTask: 'Finalizing performance analysis...',
        percentage: 100
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      if (response && response.status === 'success') {
        // After successful generation, fetch the latest performance analysis data
        // to get the complete structure
        await fetchPerformanceAnalysis(user.id, quizResults.quiz.id);
      } else {
        console.error('Failed to generate performance analysis:', response);
        setPerformanceNotFound(true);
      }
    } catch (err) {
      console.error('Error generating performance analysis:', err);
      setPerformanceNotFound(true);
    } finally {
      setIsGeneratingPerformance(false);
      setGenerationProgress({
        step: 0,
        totalSteps: 4,
        currentTask: '',
        percentage: 0
      });
    }
  };

  // Solution popup handlers
  const handleViewSolution = (questionId: number, questionImageUrl: string) => {
    setSelectedQuestionId(questionId);
    setSelectedQuestionImageUrl(questionImageUrl);
    setSolutionPopupOpen(true);
  };

  const handleCloseSolutionPopup = () => {
    setSolutionPopupOpen(false);
    setSelectedQuestionId(null);
    setSelectedQuestionImageUrl('');
  };

  const fetchQuestionSolution = async (questionId: number) => {
    return await getQuestionWithSolution(questionId);
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'danger';
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (isLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9ff' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-muted">Loading quiz results...</h5>
        </div>
      </div>
    );
  }

  if (error || !quizResults) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9ff' }}>
        <div className="text-center">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error || 'Quiz results not found'}
          </div>
          <Link to="/dashboard" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i>
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '0px' }}>
      <div className="container py-5">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="btn btn-outline-primary"
                style={{ borderRadius: '12px' }}>
                <i className="fas fa-arrow-left me-2"></i>
                Back to Dashboard
              </button>
              <h2 className="fw-bold text-primary mb-0">Quiz Results</h2>
              <div></div>
            </div>

            {/* Quiz Info Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
              <div className="card-body p-4">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h3 className="fw-bold mb-2">{quizResults.quiz.name}</h3>
                    <p className="text-muted mb-0">{quizResults.quiz.description}</p>
                    <small className="text-muted">
                      {quizResults.quiz.exam_name && `Exam: ${quizResults.quiz.exam_name} | `}
                      Subject: {quizResults.quiz.subject_name}
                    </small>
                  </div>
                  <div className="col-md-4 text-end">
                    <div className={`badge bg-${getScoreColor(quizResults.stats.percentage)} fs-4 p-3`}>
                      {quizResults.stats.percentage.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Score Summary */}
            <div className="row g-4 mb-4">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                  <div className="card-body text-center p-4">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#28a74515',
                        border: '2px solid #28a745'
                      }}>
                      <i className="fas fa-check-circle text-success fa-lg"></i>
                    </div>
                    <h3 className="fw-bold mb-1">{quizResults.stats.correct_answers}</h3>
                    <p className="text-muted mb-0">Correct Answers</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                  <div className="card-body text-center p-4">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#667eea15',
                        border: '2px solid #667eea'
                      }}>
                      <i className="fas fa-list-ol text-primary fa-lg"></i>
                    </div>
                    <h3 className="fw-bold mb-1">{quizResults.stats.total_questions}</h3>
                    <p className="text-muted mb-0">Total Questions</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                  <div className="card-body text-center p-4">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#ffc10715',
                        border: '2px solid #ffc107'
                      }}>
                      <i className="fas fa-star text-warning fa-lg"></i>
                    </div>
                    <h3 className="fw-bold mb-1">{quizResults.stats.total_score}</h3>
                    <p className="text-muted mb-0">Score Obtained</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                  <div className="card-body text-center p-4">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#dc354515',
                        border: '2px solid #dc3545'
                      }}>
                      <i className="fas fa-clock text-danger fa-lg"></i>
                    </div>
                    <h3 className="fw-bold mb-1">{formatTime(quizResults.stats.total_time_taken)}</h3>
                    <p className="text-muted mb-0">Time Taken</p>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Performance Insights */}
            {performanceAnalysis && (
              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                <div className="card-header bg-transparent border-0 p-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold mb-0">
                      <i className="fas fa-brain me-2 text-info"></i>
                      AI Performance Insights
                    </h5>
                    <button
                      className="btn btn-outline-info"
                      onClick={() => setShowPerformanceInsights(!showPerformanceInsights)}>
                      <i className={`fas ${showPerformanceInsights ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`}></i>
                      {showPerformanceInsights ? 'Hide' : 'Show'} Insights
                    </button>
                  </div>
                </div>

                {showPerformanceInsights && (
                  <div className="card-body p-4">
                    <div className="row g-4">
                      {/* Performance Metrics */}
                      <div className="col-md-6">
                        <div className="card bg-light h-100" style={{ borderRadius: '12px' }}>
                          <div className="card-body p-3">
                            <h6 className="fw-bold mb-3">
                              <i className="fas fa-chart-line me-2 text-primary"></i>
                              Performance Metrics
                            </h6>

                            <div className="row g-3">
                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted">Performance Score</span>
                                  <span className="fw-bold text-primary">
                                    {parseFloat(performanceAnalysis.performance_analysis.performance_score).toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted">Time Management</span>
                                  <span className="fw-bold text-success">
                                    {parseFloat(performanceAnalysis.performance_analysis.time_management_score).toFixed(1)}/10
                                  </span>
                                </div>
                              </div>
                              {parseFloat(performanceAnalysis.progress_tracking.accuracy_improvement) !== 0 && (
                                <div className="col-12">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Accuracy Change</span>
                                    <span className={`fw-bold ${parseFloat(performanceAnalysis.progress_tracking.accuracy_improvement) >= 0 ? 'text-success' : 'text-danger'}`}>
                                      {parseFloat(performanceAnalysis.progress_tracking.accuracy_improvement) >= 0 ? '+' : ''}
                                      {parseFloat(performanceAnalysis.progress_tracking.accuracy_improvement).toFixed(1)}%
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Strengths and Weaknesses */}
                      <div className="col-md-6">
                        <div className="card bg-light h-100" style={{ borderRadius: '12px' }}>
                          <div className="card-body p-3">
                            <h6 className="fw-bold mb-3">
                              <i className="fas fa-balance-scale me-2 text-warning"></i>
                              Strengths & Areas to Improve
                            </h6>

                            <div className="row g-3">
                              <div className="col-12">
                                <div className="mb-2">
                                  <span className="badge bg-success-subtle text-success me-2">
                                    <i className="fas fa-thumbs-up me-1"></i>
                                    Strongest
                                  </span>
                                  <small className="text-muted">
                                    {performanceAnalysis.performance_analysis.strongest_subject ||
                                      performanceAnalysis.performance_analysis.strongest_topic || 'Not identified'}
                                  </small>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="mb-2">
                                  <span className="badge bg-warning-subtle text-warning me-2">
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    Needs Focus
                                  </span>
                                  <small className="text-muted">
                                    {performanceAnalysis.performance_analysis.weakest_subject ||
                                      performanceAnalysis.performance_analysis.weakest_topic || 'Not identified'}
                                  </small>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="mb-2">
                                  <span className="badge bg-info-subtle text-info me-2">
                                    <i className="fas fa-trending-up me-1"></i>
                                    Progress
                                  </span>
                                  <small className="text-muted">
                                    {performanceAnalysis.performance_analysis.overall_progress_trend === 'improving' ? 'Improving' :
                                      performanceAnalysis.performance_analysis.overall_progress_trend === 'declining' ? 'Needs attention' :
                                        performanceAnalysis.performance_analysis.overall_progress_trend === 'stable' ? 'Consistent' : 'First attempt'}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* AI Recommendations */}
                      {performanceAnalysis.ai_insights.recommendations.length > 0 && (
                        <div className="col-12">
                          <div className="card bg-primary bg-opacity-10 border-primary" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-lightbulb me-2 text-primary"></i>
                                AI Recommendations
                              </h6>
                              <div className="row g-3">
                                {performanceAnalysis.ai_insights.recommendations.slice(0, 3).map((recommendation, index) => (
                                  <div key={index} className="col-12">
                                    <div className="d-flex align-items-start">
                                      <i className="fas fa-chevron-right text-primary me-2 mt-1"></i>
                                      <small className="text-dark">{recommendation}</small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Learning Suggestions */}
                      {performanceAnalysis.ai_insights.learning_suggestions.length > 0 && (
                        <div className="col-12">
                          <div className="card bg-success bg-opacity-10 border-success" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-graduation-cap me-2 text-success"></i>
                                Learning Suggestions
                              </h6>
                              <div className="row g-3">
                                {performanceAnalysis.ai_insights.learning_suggestions.slice(0, 3).map((suggestion, index) => (
                                  <div key={index} className="col-12">
                                    <div className="d-flex align-items-start">
                                      <i className="fas fa-chevron-right text-success me-2 mt-1"></i>
                                      <small className="text-dark">{suggestion}</small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Progress Insights */}
                      {performanceAnalysis.ai_insights.progress_insights.length > 0 && (
                        <div className="col-12">
                          <div className="card bg-info bg-opacity-10 border-info" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-chart-line me-2 text-info"></i>
                                Progress Insights ({performanceAnalysis.ai_insights.progress_insights.length})
                              </h6>
                              <div className="row g-3">
                                {performanceAnalysis.ai_insights.progress_insights.slice(0, 4).map((insight, index) => (
                                  <div key={index} className="col-12">
                                    <div className="d-flex align-items-start">
                                      <i className="fas fa-chevron-right text-info me-2 mt-1"></i>
                                      <small className="text-dark">{insight}</small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Improvement Areas */}
                      {performanceAnalysis.ai_insights.improvement_areas.length > 0 && (
                        <div className="col-12">
                          <div className="card bg-warning bg-opacity-10 border-warning" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                                Areas for Improvement ({performanceAnalysis.ai_insights.improvement_area_count})
                              </h6>
                              <div className="row g-3">
                                {performanceAnalysis.ai_insights.improvement_areas.slice(0, 4).map((area, index) => (
                                  <div key={index} className="col-md-6">
                                    <div className="d-flex align-items-start p-2 bg-white bg-opacity-50 rounded">
                                      <span className="badge bg-warning text-dark me-2" style={{ fontSize: '0.7rem' }}>
                                        {index + 1}
                                      </span>
                                      <small className="text-dark">{area}</small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Strengths Identified */}
                      {performanceAnalysis.ai_insights.strengths_identified.length > 0 && (
                        <div className="col-12">
                          <div className="card bg-success bg-opacity-10 border-success" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-star me-2 text-success"></i>
                                Identified Strengths ({performanceAnalysis.ai_insights.strength_count})
                              </h6>
                              <div className="row g-3">
                                {performanceAnalysis.ai_insights.strengths_identified.slice(0, 4).map((strength, index) => (
                                  <div key={index} className="col-md-6">
                                    <div className="d-flex align-items-start p-2 bg-white bg-opacity-50 rounded">
                                      <i className="fas fa-check-circle text-success me-2 mt-1"></i>
                                      <small className="text-dark">{strength}</small>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Learning Insights - Mastery Level & Performance Grade */}
                      <div className="col-12">
                        <div className="card bg-gradient bg-opacity-10 border" style={{ borderRadius: '12px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                          <div className="card-body p-3">
                            <h6 className="fw-bold mb-3">
                              <i className="fas fa-graduation-cap me-2 text-primary"></i>
                              Learning Mastery Assessment
                            </h6>
                            <div className="row g-3">
                              <div className="col-md-4">
                                <div className="text-center p-3 bg-white bg-opacity-75 rounded">
                                  <small className="text-muted d-block">Current Mastery Level</small>
                                  <div className={`fw-bold fs-6 ${performanceAnalysis.learning_insights.mastery_level === 'Advanced' ? 'text-success' :
                                      performanceAnalysis.learning_insights.mastery_level === 'Intermediate' ? 'text-warning' :
                                        'text-info'
                                    }`}>
                                    {performanceAnalysis.learning_insights.mastery_level}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-center p-3 bg-white bg-opacity-75 rounded">
                                  <small className="text-muted d-block">Performance Grade</small>
                                  <div className={`fw-bold fs-6 ${['A+', 'A', 'A-'].includes(performanceAnalysis.quiz_stats.performance_grade) ? 'text-success' :
                                      ['B+', 'B', 'B-'].includes(performanceAnalysis.quiz_stats.performance_grade) ? 'text-warning' :
                                        'text-danger'
                                    }`}>
                                    {performanceAnalysis.quiz_stats.performance_grade}
                                  </div>
                                  <small className="text-muted d-block">
                                    Efficiency: {(performanceAnalysis.quiz_stats.efficiency_score * 100).toFixed(1)}%
                                  </small>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-center p-3 bg-white bg-opacity-75 rounded">
                                  <small className="text-muted d-block">Difficulty Readiness</small>
                                  <div className={`badge fs-6 ${performanceAnalysis.learning_insights.difficulty_readiness === 'Ready for Higher' ? 'bg-success' :
                                      performanceAnalysis.learning_insights.difficulty_readiness === 'Current Level' ? 'bg-warning' :
                                        'bg-secondary'
                                    }`}>
                                    {performanceAnalysis.learning_insights.difficulty_readiness}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Focus Areas */}
                      {performanceAnalysis.learning_insights.focus_areas.length > 0 && (
                        <div className="col-12">
                          <div className="card bg-warning bg-opacity-10 border-warning" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-target me-2 text-warning"></i>
                                Priority Focus Areas
                              </h6>
                              <div className="row g-2">
                                {performanceAnalysis.learning_insights.focus_areas.slice(0, 4).map((area, index) => (
                                  <div key={index} className="col-md-6">
                                    <div className="d-flex justify-content-between align-items-center p-2 bg-white bg-opacity-50 rounded">
                                      <div>
                                        <small className="fw-semibold d-block">{area.area}</small>
                                        <small className="text-muted">{area.type}</small>
                                      </div>
                                      <div className="text-end">
                                        <span className={`badge ${area.priority === 'High' ? 'bg-danger' :
                                            area.priority === 'Medium' ? 'bg-warning' :
                                              'bg-success'
                                          }`} style={{ fontSize: '0.7rem' }}>
                                          {area.priority}
                                        </span>
                                        <small className="text-muted d-block">{area.score}%</small>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Study Plan & Next Steps */}
                      <div className="col-12">
                        <div className="card bg-info bg-opacity-10 border-info" style={{ borderRadius: '12px' }}>
                          <div className="card-body p-3">
                            <h6 className="fw-bold mb-3">
                              <i className="fas fa-route me-2 text-info"></i>
                              Personalized Study Plan
                            </h6>

                            {/* Study Time Recommendation */}
                            <div className="alert alert-info py-2 mb-3" style={{ borderRadius: '8px' }}>
                              <div className="row align-items-center">
                                <div className="col-md-8">
                                  <small className="fw-semibold">
                                    <i className="fas fa-clock me-2"></i>
                                    Daily Study: {performanceAnalysis.learning_insights.study_time_recommendation.daily_minutes} minutes
                                  </small>
                                </div>
                                <div className="col-md-4">
                                  <small className="text-muted">
                                    Focus: {performanceAnalysis.learning_insights.study_time_recommendation.focus}
                                  </small>
                                </div>
                              </div>
                            </div>

                            {/* Next Steps */}
                            {performanceAnalysis.learning_insights.next_steps.length > 0 && (
                              <div className="row g-2">
                                {performanceAnalysis.learning_insights.next_steps.slice(0, 3).map((step, index) => (
                                  <div key={index} className="col-12">
                                    <div className="d-flex align-items-start p-2 bg-white bg-opacity-50 rounded">
                                      <span className={`badge rounded-pill me-2 ${step.priority === 'High' ? 'bg-danger' :
                                          step.priority === 'Medium' ? 'bg-warning' :
                                            'bg-success'
                                        }`} style={{ fontSize: '0.7rem' }}>
                                        {index + 1}
                                      </span>
                                      <div className="flex-grow-1">
                                        <small className="fw-semibold d-block">{step.action}</small>
                                        <small className="text-muted">
                                          {step.priority} Priority | {step.estimated_time}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Enhanced Progress Tracking */}
                      <div className="col-12">
                        <div className="card bg-secondary bg-opacity-10 border-secondary" style={{ borderRadius: '12px' }}>
                          <div className="card-body p-3">
                            <h6 className="fw-bold mb-3">
                              <i className="fas fa-chart-line me-2 text-secondary"></i>
                              Progress Analytics
                            </h6>
                            <div className="row g-3">
                              <div className="col-md-4">
                                <div className="text-center p-2 bg-white bg-opacity-50 rounded">
                                  <small className="text-muted d-block">Progress Direction</small>
                                  <div className={`fw-semibold ${performanceAnalysis.progress_tracking.progress_direction === 'Improving' ? 'text-success' :
                                      performanceAnalysis.progress_tracking.progress_direction === 'Declining' ? 'text-danger' :
                                        'text-warning'
                                    }`}>
                                    <i className={`fas ${performanceAnalysis.progress_tracking.progress_direction === 'Improving' ? 'fa-arrow-up' :
                                        performanceAnalysis.progress_tracking.progress_direction === 'Declining' ? 'fa-arrow-down' :
                                          'fa-minus'
                                      } me-1`}></i>
                                    {performanceAnalysis.progress_tracking.progress_direction}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-center p-2 bg-white bg-opacity-50 rounded">
                                  <small className="text-muted d-block">Accuracy Trend</small>
                                  <div className={`fw-semibold ${performanceAnalysis.progress_tracking.is_improvement ? 'text-success' : 'text-warning'}`}>
                                    <i className={`fas ${performanceAnalysis.progress_tracking.is_improvement ? 'fa-thumbs-up' : 'fa-clock'} me-1`}></i>
                                    {performanceAnalysis.progress_tracking.is_improvement ? 'Improving' : 'Steady'}
                                  </div>
                                  <small className="text-muted d-block">
                                    {parseFloat(performanceAnalysis.progress_tracking.accuracy_improvement) > 0 ? '+' : ''}{performanceAnalysis.progress_tracking.accuracy_improvement}%
                                  </small>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="text-center p-2 bg-white bg-opacity-50 rounded">
                                  <small className="text-muted d-block">Time Efficiency</small>
                                  <div className={`fw-semibold ${performanceAnalysis.performance_analysis.time_efficiency_rating === 'Excellent' ? 'text-success' :
                                      performanceAnalysis.performance_analysis.time_efficiency_rating === 'Good' ? 'text-info' :
                                        performanceAnalysis.performance_analysis.time_efficiency_rating === 'Average' ? 'text-warning' :
                                          'text-danger'
                                    }`}>
                                    <i className="fas fa-tachometer-alt me-1"></i>
                                    {performanceAnalysis.performance_analysis.time_efficiency_rating}
                                  </div>
                                  <small className="text-muted d-block">
                                    {performanceAnalysis.progress_tracking.is_faster ? 'Getting Faster' : 'Consistent Pace'}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Enhanced Subject Performance Stats */}
                      {performanceAnalysis.detailed_scores.subject_count > 0 && (
                        <div className="col-12">
                          <div className="card bg-light border" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-chart-bar me-2 text-dark"></i>
                                Subject Performance Summary
                              </h6>
                              <div className="row g-3">
                                <div className="col-md-3">
                                  <div className="text-center p-2 border rounded">
                                    <small className="text-muted d-block">Best Subject</small>
                                    <div className="fw-bold text-success">{performanceAnalysis.detailed_scores.best_subject_score}%</div>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="text-center p-2 border rounded">
                                    <small className="text-muted d-block">Average Score</small>
                                    <div className="fw-bold text-info">{performanceAnalysis.detailed_scores.average_subject_score.toFixed(1)}%</div>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="text-center p-2 border rounded">
                                    <small className="text-muted d-block">Needs Work</small>
                                    <div className="fw-bold text-warning">{performanceAnalysis.detailed_scores.worst_subject_score}%</div>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="text-center p-2 border rounded">
                                    <small className="text-muted d-block">Coverage</small>
                                    <div className="fw-bold text-secondary">
                                      {performanceAnalysis.detailed_scores.subject_count}S / {performanceAnalysis.detailed_scores.topic_count}T
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Detailed Subject Scores */}
                      {performanceAnalysis.detailed_scores.subject_scores && Object.keys(performanceAnalysis.detailed_scores.subject_scores).length > 0 && (
                        <div className="col-12">
                          <div className="card bg-primary bg-opacity-10 border-primary" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-book me-2 text-primary"></i>
                                Subject-wise Performance Breakdown
                              </h6>
                              <div className="row g-2">
                                {Object.entries(performanceAnalysis.detailed_scores.subject_scores).map(([subject, score], index) => {
                                  const scoreValue = score as number;
                                  const badgeClass = scoreValue >= 80 ? 'bg-success' : scoreValue >= 60 ? 'bg-warning' : 'bg-danger';

                                  return (
                                    <div key={index} className="col-md-6 col-lg-4">
                                      <div className="d-flex justify-content-between align-items-center p-2 bg-white bg-opacity-75 rounded">
                                        <div>
                                          <small className="fw-semibold text-truncate" title={subject}>{subject}</small>
                                        </div>
                                        <div className="text-end">
                                          <span className={`badge ${badgeClass}`}>
                                            {scoreValue}%
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Detailed Topic Scores */}
                      {performanceAnalysis.detailed_scores.topic_scores && Object.keys(performanceAnalysis.detailed_scores.topic_scores).length > 0 && (
                        <div className="col-12">
                          <div className="card bg-secondary bg-opacity-10 border-secondary" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-list-ul me-2 text-secondary"></i>
                                Topic-wise Performance Breakdown
                              </h6>
                              <div className="row g-2">
                                {Object.entries(performanceAnalysis.detailed_scores.topic_scores).slice(0, 8).map(([topic, score], index) => {
                                  const scoreValue = score as number;
                                  const badgeClass = scoreValue >= 80 ? 'bg-success' : scoreValue >= 60 ? 'bg-warning' : 'bg-danger';

                                  return (
                                    <div key={index} className="col-md-6 col-lg-3">
                                      <div className="text-center p-2 bg-white bg-opacity-75 rounded">
                                        <small className="fw-semibold d-block text-truncate" title={topic}>{topic}</small>
                                        <span className={`badge mt-1 ${badgeClass}`}>
                                          {scoreValue}%
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {Object.keys(performanceAnalysis.detailed_scores.topic_scores).length > 8 && (
                                <div className="text-center mt-2">
                                  <small className="text-muted">
                                    Showing top 8 of {Object.keys(performanceAnalysis.detailed_scores.topic_scores).length} topics
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Subject Progress Tracking */}
                      {performanceAnalysis.detailed_scores.subject_progress && performanceAnalysis.detailed_scores.subject_progress.length > 0 && (
                        <div className="col-12">
                          <div className="card bg-info bg-opacity-10 border-info" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                              <h6 className="fw-bold mb-3">
                                <i className="fas fa-chart-line me-2 text-info"></i>
                                Subject Progress Over Time
                              </h6>
                              <div className="row g-2">
                                {performanceAnalysis.detailed_scores.subject_progress.slice(0, 6).map((progress, index) => (
                                  <div key={index} className="col-md-6 col-lg-4">
                                    <div className="p-2 bg-white bg-opacity-75 rounded">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <small className="fw-semibold">{progress.subject || `Subject ${index + 1}`}</small>
                                        <div className="text-end">
                                          <small className={`badge ${progress.trend === 'improving' ? 'bg-success' :
                                              progress.trend === 'declining' ? 'bg-danger' :
                                                'bg-warning'
                                            }`}>
                                            {progress.trend || 'stable'}
                                          </small>
                                          <div className="small text-muted">
                                            {progress.current_score || 'N/A'}%
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Generating Performance Analysis Progress */}
            {isGeneratingPerformance && (
              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                <div className="card-body p-4">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="d-flex align-items-center mb-3">
                        <div className="spinner-border text-info me-3" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div>
                          <h5 className="fw-bold mb-1 text-info">
                            <i className="fas fa-brain me-2"></i>
                            Generating AI Performance Analysis
                          </h5>
                          <p className="text-muted mb-0">{generationProgress.currentTask}</p>
                        </div>
                      </div>

                      {/* Progress Steps */}
                      <div className="row g-2 mb-3">
                        {[
                          { step: 1, label: 'Initialize', icon: 'fa-play-circle' },
                          { step: 2, label: 'Process Data', icon: 'fa-cog' },
                          { step: 3, label: 'AI Analysis', icon: 'fa-brain' },
                          { step: 4, label: 'Finalize', icon: 'fa-check-circle' }
                        ].map(({ step, label, icon }) => (
                          <div key={step} className="col-3">
                            <div className={`text-center p-2 rounded ${generationProgress.step >= step
                                ? 'bg-info bg-opacity-20 text-info'
                                : 'bg-light text-muted'
                              }`}>
                              <i className={`fas ${icon} d-block mb-1`}></i>
                              <small className="fw-semibold">{label}</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-md-4 text-center">
                      <div className="position-relative d-inline-flex align-items-center justify-content-center">
                        <svg className="progress-ring" width="100" height="100">
                          <circle
                            className="progress-ring-circle"
                            stroke="#e9ecef"
                            strokeWidth="8"
                            fill="transparent"
                            r="42"
                            cx="50"
                            cy="50"
                          />
                          <circle
                            className="progress-ring-circle"
                            stroke="#17a2b8"
                            strokeWidth="8"
                            fill="transparent"
                            r="42"
                            cx="50"
                            cy="50"
                            strokeDasharray={`${2 * Math.PI * 42}`}
                            strokeDashoffset={`${2 * Math.PI * 42 * (1 - generationProgress.percentage / 100)}`}
                            strokeLinecap="round"
                            style={{
                              transition: 'stroke-dashoffset 0.5s ease-in-out',
                              transform: 'rotate(-90deg)',
                              transformOrigin: '50% 50%'
                            }}
                          />
                        </svg>
                        <div className="position-absolute">
                          <div className="text-info fw-bold fs-5">{generationProgress.percentage}%</div>
                          <small className="text-muted">Complete</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="progress" style={{ height: '6px', borderRadius: '10px' }}>
                    <div
                      className="progress-bar bg-info progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${generationProgress.percentage}%`, borderRadius: '10px' }}
                      aria-valuenow={generationProgress.percentage}
                      aria-valuemin={0}
                      aria-valuemax={100}>
                    </div>
                  </div>

                  <div className="text-center mt-3">
                    <small className="text-muted">
                      <i className="fas fa-clock me-1"></i>
                      Step {generationProgress.step} of {generationProgress.totalSteps} • This may take a few moments
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Loading Performance Analysis */}
            {isLoadingPerformance && (
              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                <div className="card-body p-4 text-center">
                  <div className="spinner-border text-info me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="text-muted">Loading AI performance insights...</span>
                </div>
              </div>
            )}

            {/* Performance Analysis Not Found - Generate Button */}
            {!isLoadingPerformance && !performanceAnalysis && performanceNotFound && (
              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <i className="fas fa-brain fa-3x text-info mb-3"></i>
                    <h5 className="fw-bold mb-2">AI Performance Analysis</h5>
                    <p className="text-muted mb-0">
                      Get personalized insights about your quiz performance, strengths, and areas for improvement.
                    </p>
                  </div>

                  {/* Progress Display */}
                  {isGeneratingPerformance && (
                    <div className="mb-4">
                      <div className="d-flex justify-content-center align-items-center mb-3">
                        <div className="spinner-border text-info me-3" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="text-start">
                          <div className="fw-semibold text-primary">{generationProgress.currentTask}</div>
                          <small className="text-muted">
                            Step {generationProgress.step} of {generationProgress.totalSteps}
                          </small>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="progress mb-3" style={{ height: '8px', borderRadius: '20px' }}>
                        <div
                          className="progress-bar bg-info progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{ width: `${generationProgress.percentage}%`, borderRadius: '20px' }}
                          aria-valuenow={generationProgress.percentage}
                          aria-valuemin={0}
                          aria-valuemax={100}>
                        </div>
                      </div>

                      <div className="text-center">
                        <small className="text-info fw-semibold">
                          {generationProgress.percentage}% Complete
                        </small>
                      </div>
                    </div>
                  )}

                  <button
                    className="btn btn-info"
                    onClick={handleGeneratePerformanceAnalysis}
                    disabled={isGeneratingPerformance}
                    style={{ borderRadius: '12px' }}>
                    {isGeneratingPerformance ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Generating...</span>
                        </div>
                        Generating Analysis...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-magic me-2"></i>
                        Generate AI Performance Analysis
                      </>
                    )}
                  </button>

                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      Analysis includes personalized recommendations, learning suggestions, and progress insights
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Questions Results */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
              <div className="card-header bg-transparent border-0 p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="fw-bold mb-0">
                    <i className="fas fa-question-circle me-2 text-primary"></i>
                    Question-wise Results
                  </h5>
                  <button
                    className="btn btn-outline-info"
                    onClick={() => setShowSolutions(!showSolutions)}>
                    <i className={`fas ${showSolutions ? 'fa-eye-slash' : 'fa-eye'} me-2`}></i>
                    {showSolutions ? 'Hide Solutions' : 'Show Solutions'}
                  </button>
                </div>
              </div>
              <div className="card-body p-4">
                <div className="row g-4">
                  {quizResults.questions.map((question, index) => (
                    <div key={question.id} className="col-12">
                      <div className={`card border h-100 ${question.is_correct ? 'border-success' : 'border-danger'}`}
                        style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                          <div className="row g-4">
                            {/* Left Column - Question Image */}
                            <div className="col-md-8">
                              <h6 className="fw-bold mb-3">
                                <span className="text-muted me-2">Q{index + 1}.</span>
                                
                              </h6>
                              <div className="text-center" style={{ minHeight: '200px' }}>
                                {question.question_img_url ? (
                                  <div className="position-relative">
                                    <img
                                      src={question.question_img_url.startsWith('http')
                                        ? question.question_img_url
                                        : question.question_img_url.startsWith('/')
                                          ? `${window.location.origin}${question.question_img_url}`
                                          : `${window.location.origin}/api/${question.question_img_url}`
                                      }
                                      alt={`Question ${index + 1}`}
                                      className="img-fluid rounded border"
                                      style={{
                                        maxHeight: '350px',
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                        backgroundColor: '#f8f9fa'
                                      }}
                                      onError={(e) => {
                                        console.error('Failed to load image:', question.question_img_url);
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const parent = target.parentElement;
                                        if (parent) {
                                          parent.innerHTML = `
                                            <div class="bg-light p-4 rounded text-muted border border-dashed">
                                              <i class="fas fa-image-slash fa-3x mb-2"></i>
                                              <div class="small">Image not available</div>
                                              <div class="smaller text-truncate">${question.question_img_url}</div>
                                            </div>
                                          `;
                                        }
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="bg-light p-4 rounded text-muted border border-dashed h-100 d-flex flex-column justify-content-center">
                                    <i className="fas fa-image fa-3x mb-2"></i>
                                    <div className="small">No question image available</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Right Column - Question Data */}
                            <div className="col-md-4">
                              {/* Status Badge and Score */}
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                  <span className={`badge bg-${question.is_correct ? 'success' : 'danger'} fs-6`}>
                                    {question.is_correct ? 'Correct' : 'Incorrect'}
                                  </span>
                                </div>
                                <div className="text-end">
                                  <div className="fw-bold text-primary mb-1">
                                    Score: {question.score || 0}/4
                                  </div>
                                  <div className="small text-muted">
                                    Time: {formatTime(question.time_taken || 0)}
                                  </div>
                                </div>
                              </div>

                              {/* Answer Information */}
                              <div className="row g-3 mb-3">
                                <div className="col-6">
                                  <div className="card border-info bg-info bg-opacity-10 p-3" style={{width:'fit-content'}}>
                                    <div className="d-flex align-items-center">
                                      <div className="d-flex flex-column align-items-center justify-content-center">
                                        <strong className="mb-2">Your Answer</strong>
                                        <div className="d-flex justify-content-center">
                                          <span className={`rounded-circle border d-inline-flex align-items-center justify-content-center fw-bold ${
                                            question.is_correct 
                                              ? 'border-success bg-success text-white' 
                                              : 'border-danger bg-danger text-white'
                                          }`}
                                          style={{
                                            width: '40px',
                                            height: '40px',
                                            fontSize: '18px',
                                            minWidth: '40px'
                                          }}>
                                            {question.user_answer || '?'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-6">
                                  <div className="card border-success bg-success bg-opacity-10 p-3" style={{width:'fit-content'}}>
                                    <div className="d-flex align-items-center">
                                      <div className="d-flex flex-column">
                                        <strong className="mb-2">Correct Answer</strong>
                                        <div className="d-flex justify-content-center">
                                          <span className="rounded-circle border border-warning bg-warning text-dark d-inline-flex align-items-center justify-content-center fw-bold"
                                          style={{
                                            width: '40px',
                                            height: '40px',
                                            fontSize: '18px',
                                            minWidth: '40px'
                                          }}>
                                            {question.correct_option || question.correct_answer}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Additional Question Info */}
                              <div className="row g-3 mb-3">
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center">
                                    <i className="fas fa-list-ol text-muted me-2"></i>
                                    <small className="text-muted">
                                      Options: {question.option_count}
                                    </small>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center">
                                    <i className="fas fa-layer-group text-muted me-2"></i>
                                    <small className="text-muted">
                                      Level: {question.level || 'Not specified'}
                                    </small>
                                  </div>
                                </div>
                                <div className="col-md-12">
                                  <div className="d-flex align-items-center">
                                    <i className="fas fa-book text-muted me-2"></i>
                                    <small className="text-muted">
                                      Chapter: {question.chapter_name || 'Not specified'}
                                    </small>
                                  </div>
                                </div>
                              </div>

                              {/* View Solution Button */}
                              {question.solution && <div className="d-flex justify-content-between align-items-center mb-3">
                                <button
                                  className="btn view-solution-btn"
                                  style={{width: '100%'}}
                                  onClick={() => handleViewSolution(question.id, question.question_img_url)}
                                >
                                  <i className="fas fa-lightbulb me-2"></i>
                                  View Solution
                                </button>
                              </div>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="text-center mt-4">
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to="/dashboard"
                  className="btn btn-primary"
                  style={{ borderRadius: '12px' }}>
                  <i className="fas fa-home me-2"></i>
                  Back to Dashboard
                </Link>
                <Link
                  to="/custom-quiz-generator"
                  className="btn btn-outline-primary"
                  style={{ borderRadius: '12px' }}>
                  <i className="fas fa-plus me-2"></i>
                  Take Another Quiz
                </Link>
                <button
                  className="btn btn-outline-info"
                  onClick={() => window.print()}
                  style={{ borderRadius: '12px' }}>
                  <i className="fas fa-print me-2"></i>
                  Print Results
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Popup */}
      <SolutionPopup
        isOpen={solutionPopupOpen}
        onClose={handleCloseSolutionPopup}
        questionId={selectedQuestionId || 0}
        questionImageUrl={selectedQuestionImageUrl}
        fetchQuestionSolution={fetchQuestionSolution}
      />
    </div>
  );
};

const DetailedQuizResultsWithProvider: React.FC = () => {
  return (
    <MathJaxProvider>
      <DetailedQuizResults />
    </MathJaxProvider>
  );
};

export default DetailedQuizResultsWithProvider;
